/**
 * Session state for the dashboard.
 *
 * The token lives in localStorage so a reload does not sign the operator out.
 * That is a deliberate trade: this is a single-operator internal tool served on
 * its own origin, and the alternative (a cross-site cookie) needs credentialed
 * CORS that the separate-deployment setup makes awkward.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, setSessionExpiredHandler, setSessionToken } from "./api";

const STORAGE_KEY = "cloudreve-uploader.session";

interface StoredSession {
  token: string;
  username: string;
  expiresAt: string;
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as StoredSession;
    // An expired token would only produce a 401 on the first call; drop it now.
    if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

interface AuthValue {
  username: string | null;
  isSignedIn: boolean;
  /** True until the stored session has been checked, to avoid a login flash. */
  isRestoring: boolean;
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<StoredSession | null>(() => {
    const stored = readStoredSession();
    if (stored) setSessionToken(stored.token);
    return stored;
  });
  const [isRestoring, setIsRestoring] = useState(session !== null);

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSessionToken(null);
    setSession(null);
    setIsRestoring(false);
  }, []);

  // A 401 from anywhere in the app means the session is gone for good.
  useEffect(() => {
    setSessionExpiredHandler(signOut);
    return () => setSessionExpiredHandler(() => {});
  }, [signOut]);

  // Confirm a restored token is still accepted before showing the dashboard.
  useEffect(() => {
    if (!session || !isRestoring) return;

    let cancelled = false;
    api
      .me()
      .catch(() => {
        if (!cancelled) signOut();
      })
      .finally(() => {
        if (!cancelled) setIsRestoring(false);
      });

    return () => {
      cancelled = true;
    };
  }, [session, isRestoring, signOut]);

  const signIn = useCallback(async (username: string, password: string) => {
    const result = await api.login(username, password);
    const next: StoredSession = {
      token: result.token,
      username: result.username,
      expiresAt: result.expires_at,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setSessionToken(next.token);
    setSession(next);
    setIsRestoring(false);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      username: session?.username ?? null,
      isSignedIn: session !== null,
      isRestoring,
      signIn,
      signOut,
    }),
    [session, isRestoring, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside an AuthProvider");
  return value;
}
